import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { isHrOpsPortalView, isHrPortalView, isHrAdminPortalView, isFinancePortalView, isItPortalView, isManagerPortalView, isDeptPortalView, isExoPortalView, isSuperAdminPortalView, normalizeTychiRole } from '../utils/hrAccess';
import { APP_NAME } from '../constants';
import UserAvatar from './UserAvatar';

const Sidebar = ({ openBadge = 0, open = false, onClose = () => {} }) => {
  const { user, can, logout, accounts, activeKey, switchAccount, removeAccount } = useAuth();
  const navigate = useNavigate();
  const [switchOpen, setSwitchOpen] = useState(false);
  const [teamSize, setTeamSize] = useState(0);

  useEffect(() => {
    if (!user) { setTeamSize(0); return; }
    api.get('/my-team')
      .then((r) => setTeamSize((r.data?.team || []).length))
      .catch(() => setTeamSize(0));
  }, [user]);
  const [announceBadge, setAnnounceBadge] = useState(0);
  const [campaignBadge, setCampaignBadge] = useState(0);
  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';
  const isAdminView = user?.role === 'admin';
  const isHrOpsView = isHrOpsPortalView(user);
  const isHrView = isHrPortalView(user);
  const isHrAdminView = isHrAdminPortalView(user);
  const isFinanceView = isFinancePortalView(user);
  const isItPortalViewUser = isItPortalView(user);
  const isManagerView = isManagerPortalView(user);
  const isExoView = isExoPortalView(user);
  const isDeptPortal = isDeptPortalView(user);
  const isSuperAdminView = isSuperAdminPortalView(user);

  useEffect(() => {
    if (isExoView) return;
    api.get('/announcements/unread/count')
      .then(r => setAnnounceBadge(r.data.count))
      .catch(() => {});
  }, [isExoView]);

  useEffect(() => {
    if (isAdminView || isHrOpsView || isExoView || isSuperAdminView) return;
    api.get('/campaigns/my/pending/count')
      .then((r) => setCampaignBadge(r.data.count || 0))
      .catch(() => {});
  }, [isAdminView, isHrOpsView, isExoView, isSuperAdminView]);

  const roleLabel = (u) => {
    if (isSuperAdminPortalView(u)) return 'Super Admin';
    if (isExoPortalView(u)) return 'EXO';
    if (isHrAdminPortalView(u)) return 'HR Admin';
    if (isHrPortalView(u)) return 'HR';
    if (isManagerPortalView(u)) return 'Manager';
    if (u?.login_type === 'department') return `${u?.department} Admin`;
    if (u?.role === 'admin') return 'IT Admin';
    return normalizeTychiRole(u?.tychi_role) || 'Employee';
  };

  const portalSubtitle = () => {
    if (isAdminView) return 'IT Admin';
    if (isSuperAdminView) return 'Super Admin';
    if (isExoView) return 'EXO';
    if (isHrAdminView) return 'HR Admin';
    if (isHrOpsView) return 'Human Resources';
    if (isFinanceView) return 'Finance';
    if (isItPortalViewUser) return 'IT Support';
    if (isManagerView) return 'Manager';
    return user?.department || 'Employee Portal';
  };

  /**
   * Mirrors the rule the API enforces: admins, HR and anyone who manages
   * somebody. teamSize comes from /my-team, so a manager sees the link for the
   * same reason they see My Team. The server decides for real — this only
   * decides whether to offer the link, so it can afford to be optimistic.
   *
   * Read only by the Super Admin menu now. The other menus reach the org chart
   * through the Admin page.
   */
  const canSeeOrgChart =
    isAdminView || isSuperAdminView || isHrView || isHrAdminView || isHrOpsView || teamSize > 0;

  const navClass = ({ isActive }) => `nav-item${isActive ? ' active' : ''}`;

  const exoNav = (
    <>
      <div className="nav-section-label">EXO</div>
      {can('my_tasks') && (
        <NavLink to="/my-tasks" className={navClass}>
          <i className="ti ti-checklist nav-icon"></i> My Tasks
        </NavLink>
      )}
      {can('dashboard') && (
        <NavLink to="/dashboard" className={navClass}>
          <i className="ti ti-layout-dashboard nav-icon"></i> Dashboard
        </NavLink>
      )}
      {can('tickets') && (
        <NavLink to="/my-tickets" className={navClass}>
          <i className="ti ti-ticket nav-icon"></i> My Tickets
          {openBadge > 0 && <span className="nav-badge">{openBadge}</span>}
        </NavLink>
      )}
      {can('employees') && (
        <NavLink to="/employees" className={navClass}>
          <i className="ti ti-users nav-icon"></i> Employees
        </NavLink>
      )}
      {can('assets') && (
        <NavLink to="/assets" className={navClass}>
          <i className="ti ti-packages nav-icon"></i> Assets
        </NavLink>
      )}
      {can('attendance') && (
        <NavLink to="/attendance" className={navClass}>
          <i className="ti ti-clock-hour-4 nav-icon"></i> Attendance
        </NavLink>
      )}
      {can('leave') && (
        <NavLink to="/leave" className={navClass}>
          <i className="ti ti-calendar-off nav-icon"></i> Leave
        </NavLink>
      )}
      {(can('expenses') || can('expenses_admin')) && (
        <NavLink to="/expenses" className={navClass}>
          <i className="ti ti-currency-rupee nav-icon"></i> Expenses
        </NavLink>
      )}
    </>
  );

  const hrNav = (
    <>
      <div className="nav-section-label">{isHrAdminView ? 'HR Admin Portal' : 'HR Portal'}</div>
      {can('dashboard') && (
        <NavLink to="/dashboard" className={navClass}>
          <i className="ti ti-layout-dashboard nav-icon"></i> Dashboard
        </NavLink>
      )}
      {can('my_tasks') && (
        <NavLink to="/my-tasks" className={navClass}>
          <i className="ti ti-checklist nav-icon"></i> My Tasks
        </NavLink>
      )}
      <NavLink to="/announcements" className={navClass}>
        <i className="ti ti-speakerphone nav-icon"></i> Announcements
        {announceBadge > 0 && <span className="nav-badge">{announceBadge}</span>}
      </NavLink>
      {(isSuperAdminView || isAdminView) && (
        <NavLink to="/bugs" className={navClass}>
          <i className="ti ti-chart-histogram nav-icon"></i> Tychi Reports
        </NavLink>
      )}
      {can('tickets') && (
        <NavLink to="/my-tickets" className={navClass}>
          <i className="ti ti-ticket nav-icon"></i> My Tickets
          {openBadge > 0 && <span className="nav-badge">{openBadge}</span>}
        </NavLink>
      )}
      <NavLink to="/my-assets" className={navClass}>
        <i className="ti ti-device-laptop nav-icon"></i> My Assets
      </NavLink>
      {can('attendance') && (
        <NavLink to="/attendance" className={navClass}>
          <i className="ti ti-clock-hour-4 nav-icon"></i> Attendance
        </NavLink>
      )}
      {can('leave') && (
        <NavLink to="/leave" className={navClass}>
          <i className="ti ti-calendar-off nav-icon"></i> Leave
        </NavLink>
      )}
      {(can('expenses') || can('expenses_admin')) && (
        <NavLink to="/expenses" className={navClass}>
          <i className="ti ti-currency-rupee nav-icon"></i> Expenses
        </NavLink>
      )}
      {(can('leave') && (teamSize > 0 || can('leave_admin'))) && (
        <NavLink to="/attendance-team" className={navClass}>
          <i className="ti ti-users-group nav-icon"></i> Who Is In
        </NavLink>
      )}
      {teamSize > 0 && (
        <NavLink to="/my-team" className={navClass}>
          <i className="ti ti-users nav-icon"></i> My Team
          <span className="nav-badge">{teamSize}</span>
        </NavLink>
      )}
      {can('employees') && (
        <NavLink to="/employees" className={navClass}>
          <i className="ti ti-users nav-icon"></i> Employees
        </NavLink>
      )}
      {/* Hr Operation has this by role; anyone else needs the switch granting it */}
      {(isHrView || can('referrals')) && (
        <NavLink to="/referrals-admin" className={navClass}>
          <i className="ti ti-briefcase nav-icon"></i> Referrals &amp; Jobs
        </NavLink>
      )}
      <NavLink to="/campaigns" className={navClass}>
        <i className="ti ti-clipboard-check nav-icon"></i> Campaigns
      </NavLink>
      {can('assets') && (
        <NavLink to="/assets" className={navClass}>
          <i className="ti ti-packages nav-icon"></i> Assets
        </NavLink>
      )}
    </>
  );

  const deptPortalLabel = () => {
    if (isManagerView) return 'Manager Portal';
    if (isFinanceView) return 'Finance Portal';
    if (isItPortalViewUser) return 'IT Support Portal';
    return 'Portal';
  };

  const deptPortalNav = (
    <>
      <div className="nav-section-label">{deptPortalLabel()}</div>
      {can('dashboard') && (
        <NavLink to="/dashboard" className={navClass}>
          <i className="ti ti-layout-dashboard nav-icon"></i> Dashboard
        </NavLink>
      )}
      {can('my_tasks') && (
        <NavLink to="/my-tasks" className={navClass}>
          <i className="ti ti-checklist nav-icon"></i> My Tasks
        </NavLink>
      )}
      <NavLink to="/announcements" className={navClass}>
        <i className="ti ti-speakerphone nav-icon"></i> Announcements
        {announceBadge > 0 && <span className="nav-badge">{announceBadge}</span>}
      </NavLink>
      {(isSuperAdminView || isAdminView) && (
        <NavLink to="/bugs" className={navClass}>
          <i className="ti ti-chart-histogram nav-icon"></i> Tychi Reports
        </NavLink>
      )}
      {can('tickets') && (
        <NavLink to="/my-tickets" className={navClass}>
          <i className="ti ti-ticket nav-icon"></i> My Tickets
          {openBadge > 0 && <span className="nav-badge">{openBadge}</span>}
        </NavLink>
      )}
      <NavLink to="/my-assets" className={navClass}>
        <i className="ti ti-device-laptop nav-icon"></i> My Assets
      </NavLink>
      {can('attendance') && (
        <NavLink to="/attendance" className={navClass}>
          <i className="ti ti-clock-hour-4 nav-icon"></i> Attendance
        </NavLink>
      )}
      {can('leave') && (
        <NavLink to="/leave" className={navClass}>
          <i className="ti ti-calendar-off nav-icon"></i> Leave
        </NavLink>
      )}
      {(can('expenses') || can('expenses_admin')) && (
        <NavLink to="/expenses" className={navClass}>
          <i className="ti ti-currency-rupee nav-icon"></i> Expenses
        </NavLink>
      )}
      {(can('leave') && (teamSize > 0 || can('leave_admin'))) && (
        <NavLink to="/attendance-team" className={navClass}>
          <i className="ti ti-users-group nav-icon"></i> Who Is In
        </NavLink>
      )}
      {teamSize > 0 && (
        <NavLink to="/my-team" className={navClass}>
          <i className="ti ti-users nav-icon"></i> My Team
          <span className="nav-badge">{teamSize}</span>
        </NavLink>
      )}
      {can('employees') && (
        <NavLink to="/employees" className={navClass}>
          <i className="ti ti-users nav-icon"></i> Employees
        </NavLink>
      )}
      {can('assets') && (
        <NavLink to="/assets" className={navClass}>
          <i className="ti ti-packages nav-icon"></i> Assets
        </NavLink>
      )}
      {/*
        My Team and Org Chart appear once, above Employees. They were listed a
        second time here as well, so any manager saw both entries twice —
        visible to anybody with reports, which is most managers.
      */}
      <NavLink to="/my-forms" className={navClass}>
        <i className="ti ti-file-text nav-icon"></i> My Forms
      </NavLink>
      <NavLink to="/campaigns" className={navClass}>
        <i className="ti ti-clipboard-check nav-icon"></i> Campaigns
        {campaignBadge > 0 && <span className="nav-badge">{campaignBadge}</span>}
      </NavLink>
    </>
  );

  const superAdminNav = (
    <>
      <div className="nav-section-label">Super Admin</div>
      {can('dashboard') && (
        <NavLink to="/dashboard" className={navClass}>
          <i className="ti ti-layout-dashboard nav-icon"></i> Dashboard
        </NavLink>
      )}
      <NavLink to="/announcements" className={navClass}>
        <i className="ti ti-speakerphone nav-icon"></i> Announcements
        {announceBadge > 0 && <span className="nav-badge">{announceBadge}</span>}
      </NavLink>
      {(isSuperAdminView || isAdminView) && (
        <NavLink to="/bugs" className={navClass}>
          <i className="ti ti-chart-histogram nav-icon"></i> Tychi Reports
        </NavLink>
      )}
      {can('tickets') && (
        <NavLink to="/all-tickets" className={navClass}>
          <i className="ti ti-ticket nav-icon"></i> All Tickets
          {openBadge > 0 && <span className="nav-badge">{openBadge}</span>}
        </NavLink>
      )}
      {can('assets') && (
        <NavLink to="/assets" className={navClass}>
          <i className="ti ti-packages nav-icon"></i> Assets
        </NavLink>
      )}
      {teamSize > 0 && (
        <NavLink to="/my-team" className={navClass}>
          <i className="ti ti-users nav-icon"></i> My Team
          <span className="nav-badge">{teamSize}</span>
        </NavLink>
      )}
        {can('attendance') && (
          <NavLink to="/attendance" className={navClass}>
            <i className="ti ti-clock-hour-4 nav-icon"></i> Attendance
          </NavLink>
        )}
        {can('leave') && (
          <NavLink to="/leave" className={navClass}>
            <i className="ti ti-calendar-off nav-icon"></i> Leave
          </NavLink>
        )}
        {(can('expenses') || can('expenses_admin')) && (
          <NavLink to="/expenses" className={navClass}>
            <i className="ti ti-currency-rupee nav-icon"></i> Expenses
          </NavLink>
        )}
        {(can('leave') && (teamSize > 0 || can('leave_admin'))) && (
          <NavLink to="/attendance-team" className={navClass}>
            <i className="ti ti-users-group nav-icon"></i> Who Is In
          </NavLink>
        )}
      {canSeeOrgChart && (
        <NavLink to="/org-chart" className={navClass}>
          <i className="ti ti-sitemap nav-icon"></i> Org Chart
        </NavLink>
      )}

      <div className="nav-section-label">HR</div>
      {can('employees') && (
        <NavLink to="/employees" className={navClass}>
          <i className="ti ti-users nav-icon"></i> Employees
        </NavLink>
      )}
      {can('employee_details') && (
        <NavLink to="/onboarding" className={navClass}>
          <i className="ti ti-user-plus nav-icon"></i> Onboarding
        </NavLink>
      )}
      {can('employee_details') && (
        <NavLink to="/offboarding" className={navClass}>
          <i className="ti ti-user-minus nav-icon"></i> Offboarding
        </NavLink>
      )}
      <NavLink to="/referrals-admin" className={navClass}>
        <i className="ti ti-briefcase nav-icon"></i> Referrals &amp; Jobs
      </NavLink>
      <NavLink to="/campaigns" className={navClass}>
        <i className="ti ti-clipboard-check nav-icon"></i> Campaigns
      </NavLink>

      {/* No Audit Log entry here by design — it was removed deliberately.
          The /audit route still exists and works if you navigate to it directly.
          QA flags this as AUD-05; it is intentional, not a missing link. */}
    </>
  );

  return (
    <aside className={`sidebar${open ? ' open' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-icon" aria-hidden="true">
          <img src="/brand/tychi-mark.png" alt="" className="brand-icon-img" />
        </div>
        <div className="brand-text">
          <div className="brand-name">{APP_NAME}</div>
          <div className="brand-sub">{portalSubtitle()}</div>
        </div>
        <button type="button" className="sidebar-close-btn" onClick={onClose} title="Close menu">
          <i className="ti ti-x"></i>
        </button>
      </div>

      <nav className="sidebar-nav" onClick={(e) => { if (e.target.closest('.nav-item')) onClose(); }}>
        {isExoView && exoNav}

        {!isExoView && isSuperAdminView && superAdminNav}

        {!isExoView && !isSuperAdminView && isHrOpsView && hrNav}

        {!isExoView && !isSuperAdminView && isDeptPortal && !isHrOpsView && deptPortalNav}

        {!isExoView && !isSuperAdminView && !isAdminView && !isHrOpsView && !isDeptPortal && (
          <>
            {can('dashboard') && (
              <>
                <div className="nav-section-label">Menu</div>
                <NavLink to="/dashboard" className={navClass}>
                  <i className="ti ti-layout-dashboard nav-icon"></i> Dashboard
                </NavLink>
              </>
            )}

            {can('my_tasks') && (
              <NavLink to="/my-tasks" className={navClass}>
                <i className="ti ti-checklist nav-icon"></i> My Tasks
              </NavLink>
            )}

            <NavLink to="/announcements" className={navClass}>
              <i className="ti ti-speakerphone nav-icon"></i> Announcements
              {announceBadge > 0 && <span className="nav-badge">{announceBadge}</span>}
            </NavLink>
            {(isSuperAdminView || isAdminView) && (
              <NavLink to="/bugs" className={navClass}>
                <i className="ti ti-chart-histogram nav-icon"></i> Tychi Reports
              </NavLink>
            )}

            {can('tickets') && (
              <NavLink to="/my-tickets" className={navClass}>
                <i className="ti ti-ticket nav-icon"></i> My Tickets
                {openBadge > 0 && <span className="nav-badge">{openBadge}</span>}
              </NavLink>
            )}

            <NavLink to="/my-assets" className={navClass}>
              <i className="ti ti-device-laptop nav-icon"></i> My Assets
            </NavLink>
            {can('attendance') && (
              <NavLink to="/attendance" className={navClass}>
                <i className="ti ti-clock-hour-4 nav-icon"></i> Attendance
              </NavLink>
            )}
            {can('leave') && (
              <NavLink to="/leave" className={navClass}>
                <i className="ti ti-calendar-off nav-icon"></i> Leave
              </NavLink>
            )}
            {(can('expenses') || can('expenses_admin')) && (
              <NavLink to="/expenses" className={navClass}>
                <i className="ti ti-currency-rupee nav-icon"></i> Expenses
              </NavLink>
            )}
            {(can('leave') && (teamSize > 0 || can('leave_admin'))) && (
              <NavLink to="/attendance-team" className={navClass}>
                <i className="ti ti-users-group nav-icon"></i> Who Is In
              </NavLink>
            )}


            {teamSize > 0 && (
              <NavLink to="/my-team" className={navClass}>
                <i className="ti ti-users nav-icon"></i> My Team
                <span className="nav-badge">{teamSize}</span>
              </NavLink>
            )}

            <NavLink to="/my-forms" className={navClass}>
              <i className="ti ti-file-text nav-icon"></i> My Forms
            </NavLink>

            <NavLink to="/campaigns" className={navClass}>
              <i className="ti ti-clipboard-check nav-icon"></i> Campaigns
              {campaignBadge > 0 && <span className="nav-badge">{campaignBadge}</span>}
            </NavLink>

            <NavLink to="/referrals" className={navClass}>
              <i className="ti ti-briefcase nav-icon"></i> Referrals
            </NavLink>
          </>
        )}

        {!isExoView && isAdminView && (
          <>
            {can('dashboard') && (
              <>
                <div className="nav-section-label">IT Admin</div>
                <NavLink to="/dashboard" className={navClass}>
                  <i className="ti ti-layout-dashboard nav-icon"></i> Dashboard
                </NavLink>
              </>
            )}

            {can('my_tasks') && (
              <NavLink to="/my-tasks" className={navClass}>
                <i className="ti ti-checklist nav-icon"></i> My Tasks
              </NavLink>
            )}

            {/*
              My Team and Org Chart are rendered once, higher up in this same
              block, next to Leave Approvals. A second copy appeared here, so
              anybody who both manages people and sees the org chart — every
              manager — got each link twice in one menu.
            */}

            {can('attendance') && (
              <NavLink to="/attendance" className={navClass}>
                <i className="ti ti-clock-hour-4 nav-icon"></i> Attendance
              </NavLink>
            )}
            {can('leave') && (
              <NavLink to="/leave" className={navClass}>
                <i className="ti ti-calendar-off nav-icon"></i> Leave
              </NavLink>
            )}
            {(can('expenses') || can('expenses_admin')) && (
              <NavLink to="/expenses" className={navClass}>
                <i className="ti ti-currency-rupee nav-icon"></i> Expenses
              </NavLink>
            )}
            {(can('leave') && (teamSize > 0 || can('leave_admin'))) && (
              <NavLink to="/attendance-team" className={navClass}>
                <i className="ti ti-users-group nav-icon"></i> Who Is In
              </NavLink>
            )}
            {can('tickets') && (
              <NavLink to="/all-tickets" className={navClass}>
                <i className="ti ti-ticket nav-icon"></i> All Tickets
                {openBadge > 0 && <span className="nav-badge">{openBadge}</span>}
              </NavLink>
            )}

            {can('assets') && (
              <NavLink to="/assets" className={navClass}>
                <i className="ti ti-packages nav-icon"></i> Assets
              </NavLink>
            )}

            <NavLink to="/announcements" className={navClass}>
              <i className="ti ti-speakerphone nav-icon"></i> Announcements
              {announceBadge > 0 && <span className="nav-badge">{announceBadge}</span>}
            </NavLink>
            {(isSuperAdminView || isAdminView) && (
              <NavLink to="/bugs" className={navClass}>
                <i className="ti ti-chart-histogram nav-icon"></i> Tychi Reports
              </NavLink>
            )}

            {can('employees') && (
              <>
                <div className="nav-section-label">Operations</div>
                <NavLink to="/employees" className={navClass}>
                  <i className="ti ti-users nav-icon"></i> Employees
                </NavLink>
              </>
            )}

            <NavLink to="/campaigns" className={navClass}>
              <i className="ti ti-clipboard-check nav-icon"></i> Campaigns
            </NavLink>

          </>
        )}
      </nav>

      <div className="sidebar-footer">
        <div style={{ position: 'relative' }}>
          {switchOpen && (
            <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', marginBottom: 8, overflow: 'hidden', zIndex: 200 }}>
              <div style={{ padding: '9px 14px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Accounts
              </div>

              {accounts.map(acc => {
                const isActive = acc.key === activeKey;
                const ini = acc.user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';
                return (
                  <div
                    key={acc.key}
                    onClick={() => { if (!isActive) switchAccount(acc.key); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: isActive ? 'default' : 'pointer', background: isActive ? 'var(--bg)' : 'none', borderTop: '1px solid var(--bg2)' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none'; }}
                  >
                    <div className="user-avatar" style={{ width: 30, height: 30, fontSize: 12 }}>{ini}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{acc.user?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{roleLabel(acc.user)}</div>
                    </div>
                    {isActive
                      ? <i className="ti ti-check" style={{ fontSize: 16, color: 'var(--blue)' }}></i>
                      : <button
                          type="button"
                          title="Remove account"
                          onClick={(e) => { e.stopPropagation(); removeAccount(acc.key); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: 4, display: 'flex' }}
                        >
                          <i className="ti ti-x"></i>
                        </button>}
                  </div>
                );
              })}

              <div
                onClick={() => { setSwitchOpen(false); navigate('/login?add=1'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', borderTop: '1px solid var(--bg2)', color: 'var(--blue)', fontWeight: 500, fontSize: 13 }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <i className="ti ti-plus" style={{ fontSize: 15 }}></i> Add another account
              </div>

              <div
                onClick={() => { setSwitchOpen(false); navigate('/profile'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', borderTop: '1px solid var(--bg2)', color: 'var(--text2)', fontSize: 13 }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <i className="ti ti-user" style={{ fontSize: 15 }}></i> View profile
              </div>
            </div>
          )}

          <div className="user-card" onClick={() => setSwitchOpen(o => !o)}>
            <UserAvatar className="user-avatar" initials={initials} alt="" />
            <div className="user-info">
              <div className="user-name" title={user?.name}>{user?.name}</div>
              <div className="user-role">{roleLabel(user)}</div>
            </div>
            <div className="user-card-actions">
              <i className={`ti ti-chevron-${switchOpen ? 'up' : 'down'} user-card-chevron`}></i>
              <button
                type="button"
                className="logout-btn"
                title="Logout"
                aria-label="Logout"
                onClick={(e) => { e.stopPropagation(); logout(); }}
              >
                <i className="ti ti-logout"></i>
              </button>
            </div>
          </div>

          <button
            type="button"
            className="sidebar-logout-row"
            onClick={() => { onClose(); logout(); }}
          >
            <i className="ti ti-logout" />
            Log out
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
